/*
 * Copyright (c) 2020 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { Injectable, Injector } from '@angular/core';
import { ViewService } from '@angular-kit/view';

import { CoreActionKey } from '@typescript-kit/core';
import { TimeClockShellViewService } from './time-clock-shell.view-service';
import { ActivatedRoute } from '@angular/router';
import {
  ActionModel,
  ContainerModel,
  TextModel,
} from '@typescript-kit/view';
import { TimeClockViewKey } from '../key/time-clock-view.key';
import { TimeClockRegisterViewData, TimeClockRegisterViewModel } from './time-clock-register.view-model';

@Injectable()
export class TimeClockRegisterViewService extends ViewService<TimeClockRegisterViewModel, ContainerModel> {
  private static readonly CLOSE_TIMEOUT = 30 * 1000;
  private readonly timeClockShellViewService: TimeClockShellViewService;
  private backTimeoutHandle: any;

  constructor(
    injector: Injector,
    private readonly route: ActivatedRoute
  ) {
    super(injector);
    this.timeClockShellViewService = injector.get(TimeClockShellViewService);
  }

  get timeClockId(): string {
    return this.timeClockShellViewService.viewModel.timeClockId;
  }

  initialize(data?: TimeClockRegisterViewData): void {
    const viewModel = (data && data instanceof TimeClockRegisterViewModel) ? data : new TimeClockRegisterViewModel(data);
    this.route.paramMap.subscribe((paramMap) => {
      viewModel.setValues({
        token: paramMap.get('token')
      });
      this.scheduleBackTimeout();
    });
    super.initialize(viewModel);
  }

  finalize(): void {
    this.clearBackTimeout();
    super.finalize();
  }

  private back(): void {
    this.timeClockShellViewService.viewModel.nfcKey = null;
  }

  private scheduleBackTimeout() {
    this.clearBackTimeout();
    this.backTimeoutHandle = setTimeout(() => {
      this.backTimeoutHandle = null;
      this.back();
    }, TimeClockRegisterViewService.CLOSE_TIMEOUT);
  }

  private clearBackTimeout() {
    if (!this.backTimeoutHandle) {
      return;
    }
    clearTimeout(this.backTimeoutHandle);
    this.backTimeoutHandle = null;
  }

  protected createComponentModel(): ContainerModel {
    return new ContainerModel({
      items: [
        this.createHeaderTextModel(),
        this.createMessageTextModel(),
        this.createHintTextModel(),
        this.createTokenTextModel(),
        this.createBackActionModel()
      ]
    });
  }

  private createHeaderTextModel(): TextModel {
    return new TextModel({
      key: 'headerText', tags: ['app-header-text'],
      text: `${TimeClockViewKey.REGISTER}/header`
    });
  }

  private createMessageTextModel(): TextModel {
    return new TextModel({
      key: 'messageText', tags: ['app-message-text'],
      text: `${TimeClockViewKey.REGISTER}/message`
    });
  }

  private createHintTextModel(): TextModel {
    return new TextModel({
      key: 'hintText', tags: ['app-hint-text'],
      text: `${TimeClockViewKey.REGISTER}/hint`
    });
  }

  private createTokenTextModel(): TextModel {
    return new TextModel({
      key: 'tokenText', tags: ['app-token-text'],
      text: this.viewModel.token
    });
  }

  private createBackActionModel(): ActionModel {
    return new ActionModel({
      key: 'backAction', tags: ['app-back-action', 'btn-primary'],
      content: CoreActionKey.BACK,
      onClick: () => this.back()
    });
  }

}
